/**
 * Goal Game — the star-player pick.
 *
 * Two pieces, both dumb: the LIST the fan taps during registration, and the
 * BAR that sits under every fan screen afterwards. Neither one calls the
 * server; the fan page owns all the wiring.
 *
 * The LIST is grouped by tonight's two TEAMS, each team's players A to Z —
 * the owner's own override of the old "no team name" rule, for this list only.
 * No league name appears here. A row is a person: name, number, position.
 */
import { useMemo, useState } from 'react';
import { Check, Search, Star } from 'lucide-react';

const fold = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const StarList = ({ players, selected, onSelect, c, field, t, searchLabel }) => {
    const [q, setQ] = useState('');

    const groups = useMemo(() => {
        const needle = fold(q.trim());
        const hits = (players || []).filter((p) => !needle
            || fold(p.name).includes(needle)
            || fold(p.position).includes(needle)
            || fold(p.team).includes(needle)
            || (p.number || '').startsWith(needle));
        // One header per TEAM, in the order the feed lists them, with that
        // team's players A to Z.
        const out = new Map();
        hits.forEach((p) => {
            const key = p.team || t('goalGame.starOtherTeam');
            if (!out.has(key)) out.set(key, []);
            out.get(key).push(p);
        });
        return [...out.entries()].map(([team, rows]) => [
            team, [...rows].sort((a, b) => fold(a.name).localeCompare(fold(b.name))),
        ]);
    }, [players, q, t]);

    return (
        <div data-testid="goal-star-list">
            <label className="relative block mb-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder={searchLabel}
                       aria-label={searchLabel}
                       className={`${field} pl-9`} data-testid="goal-star-search" />
            </label>
            {/* Short enough on a phone that Save, Back and Skip stay on
                screen under the list, with the score bar below them. */}
            <div className="max-h-[34vh] sm:max-h-[44vh] overflow-y-auto -mx-1 px-1 space-y-4">
                {groups.length === 0 && (
                    <p className="text-sm opacity-60 py-4 text-center" data-testid="goal-star-empty-search">
                        {t('goalGame.starNoMatch')}
                    </p>
                )}
                {groups.map(([team, rows]) => (
                    <div key={team}>
                        <p className="text-[11px] font-semibold uppercase tracking-wider opacity-50 mb-1"
                           data-testid={`goal-star-group-${fold(team).replace(/\s+/g, '-')}`}>
                            {team}
                        </p>
                        <div className="space-y-1.5">
                            {rows.map((p) => {
                                const on = selected?.id === p.id;
                                return (
                                    <button key={p.id} type="button" onClick={() => onSelect(p)}
                                            aria-pressed={on}
                                            data-testid={`goal-star-row-${p.id}`}
                                            style={on ? { borderColor: c.accent, backgroundColor: 'rgba(255,255,255,0.12)' } : undefined}
                                            className="w-full flex items-center gap-3 rounded-xl border border-white/15 px-3 py-3 text-left min-h-[48px]">
                                        <span className="w-9 shrink-0 text-center text-sm font-black tabular-nums"
                                              style={{ color: c.accent }}>
                                            {p.number ? `#${p.number}` : '—'}
                                        </span>
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm font-semibold truncate">{p.name}</span>
                                            {p.position && (
                                                <span className="block text-[11px] opacity-60">{p.position}</span>
                                            )}
                                        </span>
                                        {on && <Check size={16} style={{ color: c.accent }} />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/** In the page's flow, NOT fixed — it can never cover a button or the scratch
 *  card, at 390px or on an iPad. It is the last thing on every screen. */
export const StarBar = ({ star, label, emptyLabel, onOpen, onButtonTap, c, locked = true }) => (
    <div className="mt-5" data-testid="goal-star-bar">
        <div
            role={!star && onOpen ? 'button' : undefined}
            tabIndex={!star && onOpen ? 0 : undefined}
            onClick={!star && onOpen ? () => { try { onButtonTap?.(); } catch (_e) { /* never blocks */ } onOpen(); } : undefined}
            data-locked={star && locked ? 'true' : 'false'}
            className="flex items-center gap-3 rounded-2xl border border-white/15 bg-black/25 px-4 py-3"
        >
            <Star size={16} style={{ color: c.accent }} className="shrink-0" />
            {star ? (
                <span className="min-w-0">
                    <span className="block text-[10px] font-semibold uppercase tracking-wider opacity-60"
                          data-testid="goal-star-bar-label">{label}</span>
                    <span className="block text-sm font-bold truncate" data-testid="goal-star-bar-name">
                        {star.name}{star.number ? ` #${star.number}` : ''}
                    </span>
                </span>
            ) : (
                <span className="text-sm font-semibold" data-testid="goal-star-bar-empty">{emptyLabel}</span>
            )}
        </div>
    </div>
);
